import { useState, type JSX } from "react";
import { Paperclip, X } from "react-feather";
import { useLocalization } from "@fluent/react";

import ApiHttp from "byzantine/src/ApiHttp";
import Form from "../Form";
import { openFileDialog, uploadFile } from "../../common/file_utils";
import "./ThreadReply.css";

type ThreadReplyProps = {
  threadId: string;
  reloadThread?: () => void;
  replyDisclaimer?: string;
  replyTime?: string;
  userMessageUploads?: boolean;
};

type ThreadReplyData = {
  messageBody: string;
};

type Attachment = {
  file: File;
  id: string;
};

type FormAction = {
  label: string;
  onSubmit: (completedCallback: (err?: string) => void) => Promise<void>;
  successNotification?: {
    type: string;
    text: string;
  };
  failNotification?: {
    type: string;
    text: string;
  };
  fancyLabel?: JSX.Element;
  className?: string;
  noValidation?: boolean;
};

type MessagePayload =
  | { body: string; file?: unknown }
  | { body?: string; file: unknown };

export default function ThreadReply({
  threadId,
  reloadThread = () => {},
  replyDisclaimer,
  replyTime,
  userMessageUploads,
}: ThreadReplyProps) {
  const { l10n } = useLocalization();
  const [data, setData] = useState<ThreadReplyData>({ messageBody: "" });
  const [attachments, setAttachments] = useState<Attachment[]>([]);

  const updateData = (update: Partial<ThreadReplyData>) => {
    setData((prev) => Object.assign({}, prev, update));
  };

  const removeAttachment = (attachmentId: string) => {
    setAttachments((prev) =>
      prev.filter((attachment) => attachment.id !== attachmentId)
    );
  };

  const postMessage = async (payload: MessagePayload) => {
    await ApiHttp.fetch(
      `threads/${threadId}/messages`,
      { method: "POST" },
      payload
    );
    reloadThread();
  };

  const sendMessage = async (completedCallback: (err?: string) => void) => {
    try {
      if (!data.messageBody.trim() && attachments.length === 0) {
        return;
      }

      // Send all attachments first (if any)
      if (attachments.length > 0) {
        // Process attachments sequentially
        await attachments.reduce<Promise<void>>(
          async (previousPromise, attachment) => {
            await previousPromise;
            const file = await uploadFile(attachment.file);
            return postMessage({ file });
          },
          Promise.resolve()
        );
      }

      // Then send the message body (if any)
      if (data.messageBody.trim()) {
        await postMessage({ body: data.messageBody });
      }

      // Clear message body and attachments after successful send
      updateData({ messageBody: "" });
      setAttachments([]);
      completedCallback();
    } catch (err) {
      completedCallback(
        l10n.getString(
          "error-message-send-failed",
          null,
          "There was a problem sending your message."
        )
      );
    }
  };

  const attachFile = async (completedCallback: (err?: string) => void) => {
    try {
      const file = await openFileDialog();
      if (file) {
        // Create a new file with spaces replaced by underscores in the name
        const sanitizedFileName = file.name.replace(/\s+/g, "_");
        const sanitizedFile = new File([file], sanitizedFileName, {
          type: file.type,
          lastModified: file.lastModified,
        });

        // Add file to attachments state instead of immediately uploading
        setAttachments((prev) => [
          ...prev,
          {
            file: sanitizedFile,
            id: `${Math.random().toString(36)}-${Date.now()}`,
          },
        ]);
      }
      completedCallback();
    } catch (err) {
      completedCallback(
        l10n.getString(
          "error-file-attach-failed",
          null,
          "There was a problem attaching your file."
        )
      );
    }
  };

  function getValidActions(): FormAction[] {
    const validActions: FormAction[] = [
      {
        label: l10n.getString("button-thread-send", null, "Send"),
        onSubmit: sendMessage,
        successNotification: {
          type: "success",
          text: l10n.getString("message-notif-sent", null, "Message sent."),
        },
        failNotification: {
          type: "negative",
          text: l10n.getString(
            "error-message-send-failed",
            null,
            "There was a problem sending your message."
          ),
        },
      },
    ];

    if (userMessageUploads) {
      validActions.push({
        label: l10n.getString("button-thread-attach", null, "Attach"),
        fancyLabel: <Paperclip />,
        className: "ui button message-icon-button",
        onSubmit: attachFile,
        noValidation: true,
        failNotification: {
          type: "negative",
          text: l10n.getString(
            "error-file-attach-failed",
            null,
            "There was a problem attaching your file."
          ),
        },
      });
    }

    return validActions;
  }

  return (
    <div className="ui form thread-reply">
      <Form
        data={data}
        onChange={updateData}
        actions={getValidActions()}
        actionBarStyle={{
          display: "flex",
          flexDirection: "row-reverse",
          marginTop: "20px",
        }}
      >
        <textarea
          className="fluid field"
          placeholder={l10n.getString(
            "placeholder-reply-message",
            null,
            "Reply to message..."
          )}
          {...{ field: "messageBody" }}
        />

        {/* File attachments preview */}
        {attachments.length > 0 && (
          <div className="attachments-preview">
            <div className="attachments-list">
              {attachments.map((attachment) => (
                <div key={attachment.id} className="attachment-label">
                  <Paperclip size={14} className="attachment-icon" />
                  <span
                    className="attachment-filename"
                    title={attachment.file.name}
                  >
                    {attachment.file.name}
                  </span>
                  <X
                    size={14}
                    className="attachment-remove-icon"
                    onClick={() => removeAttachment(attachment.id)}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </Form>
      <div className="thread-reply-expected-reply-time">
        {replyTime
          ? `Typically replies within ${replyTime.toLowerCase()}.`
          : ""}
      </div>
      <div className="thread-reply-expected-reply-time">{replyDisclaimer}</div>
    </div>
  );
}
